import React from 'react';
import { ExternalLink, MessageCircle, Store } from 'lucide-react';
import { StatusBadge } from '../../../components/deliveries/StatusBadge';
import { CopyableText } from '../../../components/ui/CopyableText';
import { getShippingMethodLabel } from '../../../utils/catalogs';
import {
  getFullAddress,
  getOrderProvider,
  getOrderTypeLabel,
} from '../../../features/orderDetails/orderUtils';
import {
  buildSupportWhatsappLink,
  currentStatusOf,
} from '../../../features/tracking/trackingUtils';
import { COLLECT_ORDER_TYPES } from '../../../features/orderDetails/orderConstants';

const Fact = ({ label, children }) => (
  <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
    <dt className="text-xs font-bold uppercase tracking-wide text-text-muted sm:w-44 sm:shrink-0">
      {label}
    </dt>
    <dd className="text-sm text-ninja-dark dark:text-white break-words">{children}</dd>
  </div>
);

/**
 * The card that answers "what did I buy and where is it".
 *
 * The delivery address is on it. It came off for a while — this page is public
 * and reached by a sequential order id, so it is the most valuable thing here
 * to anybody who is not the recipient — and it is back because customers asked:
 * they use it to confirm the parcel is headed where they said, and a delivery
 * that failed on `incomplete_address` reads as nonsense without it.
 *
 * A collect order shows the shop counter instead, which is a commercial address
 * and the whole point of the trip. The old page branched only on
 * `click_collect`, which left a Pay & Collect order showing nothing at all.
 *
 * @param {object} props
 * @param {object} props.order - As `customer_track_order` returns it.
 */
export const TrackingSummary = ({ order }) => {
  const isCollect = COLLECT_ORDER_TYPES.includes(order.type);
  const delivery = order.delivery;
  const current = currentStatusOf(order);
  const whatsappLink = buildSupportWhatsappLink(order, order.id);

  return (
    <section
      data-testid="tracking-summary"
      className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border p-6 sm:p-8"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-ninja-dark dark:text-white">
            Pedido #{order.id}
          </h2>
          {order.seller?.company?.social_reason && (
            <p className="text-sm text-text-secondary flex items-center gap-1.5 mt-1">
              <Store size={14} />
              {order.seller.company.social_reason}
            </p>
          )}
        </div>

        {current && (
          <StatusBadge
            variant="strong"
            status={current.status}
            substatus={current.substatus}
            testId="tracking-current-status"
          />
        )}
      </div>

      <dl className="space-y-4">
        {order.reference_id && (
          <Fact label="Referencia">{order.reference_id}</Fact>
        )}
        <Fact label="Tipo de pedido">{getOrderTypeLabel(order.type)}</Fact>

        {isCollect ? (
          <>
            <Fact label="Punto de retiro">{order.warehouse?.name || '-'}</Fact>
            <Fact label="Dirección de retiro">
              {getFullAddress(order.warehouse?.address)}
            </Fact>
          </>
        ) : (
          <>
            <Fact label="Destinatario">{order.recipient || '-'}</Fact>
            <Fact label="Tipo de entrega">
              {delivery?.shipping_method ? getShippingMethodLabel(delivery.shipping_method) : '-'}
            </Fact>
            <Fact label="Dirección de entrega">
              {getFullAddress(delivery?.shipping_address)}
            </Fact>
            <Fact label="Transportista">{getOrderProvider(delivery)}</Fact>
            <Fact label="Bultos">
              {delivery?.packages?.trim() ? delivery.packages : '1'}
            </Fact>
            {delivery?.courier_tracking_number && (
              <Fact label="N° de seguimiento">
                <CopyableText
                  value={delivery.courier_tracking_number}
                  maxWidth="220px"
                  className="text-sm text-ninja-dark dark:text-white"
                />
              </Fact>
            )}
          </>
        )}
      </dl>

      <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-border">
        {delivery?.url_tracking && (
          <a
            href={delivery.url_tracking}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tracking-courier-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-ninja-green hover:bg-ninja-green/90 text-white shadow-sm transition-colors"
          >
            <ExternalLink size={16} />
            Seguir en el transportista
          </a>
        )}

        {/* WhatsApp's brand pair, white on #25D366, chosen deliberately: the
            button has to be recognisable as WhatsApp at a glance, and that is
            the combination people know it by. It measures 1.98:1, below the
            4.5:1 the rest of this page holds to — a knowing trade, not an
            oversight, so leave it unless hirochi says otherwise. The same pair
            in both themes, because the green does not flip. */}
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="tracking-whatsapp-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white bg-[#25D366] hover:bg-[#1EBE5B] shadow-sm transition-colors"
        >
          <MessageCircle size={16} />
          Consultar por WhatsApp
        </a>
      </div>
    </section>
  );
};
